class CardVideo extends HTMLElement {
	constructor() {
		super();

		this.video = null;
		this.viewportObserver = null;
		this.sliderBound = false;

		this.querySelector(".card__media-video-play")?.addEventListener(
			"click",
			this.onPlayClick.bind(this)
		);

		this.dropPlaceholderWhenPosterLoads();
	}

	// The placeholder behind the poster bleeds a grey hairline along the card
	// edges once the card lands on a fractional width - same as blur-up.js.
	dropPlaceholderWhenPosterLoads() {
		const poster = this.querySelector("img");
		if (!poster) return;

		const clear = () => {
			this.style.backgroundImage = "none";
		};

		if (poster.complete && poster.naturalWidth) clear();
		else poster.addEventListener("load", clear, { once: true });
	}

	disconnectedCallback() {
		this.viewportObserver?.disconnect();
		this.viewportObserver = null;
	}

	onPlayClick(event) {
		// The whole slide is covered by the link to the product page, so the play
		// button must not bubble up to it.
		event.preventDefault();
		event.stopPropagation();

		this.loadVideo();
	}

	loadVideo() {
		if (this.video) {
			this.playVideo();
			return;
		}

		const template = this.querySelector("template");
		if (!template) return;

		const video = template.content.firstElementChild?.cloneNode(true);
		if (!video || video.tagName !== "VIDEO") return;

		this.pauseOtherMedia();

		// Autoplay is only allowed while muted; the controls let the customer
		// turn the sound on.
		video.muted = true;
		video.setAttribute("muted", "");
		video.setAttribute("playsinline", "");
		video.setAttribute("preload", "auto");

		this.appendChild(video);
		this.video = video;
		this.setAttribute("loaded", "true");

		video.load();
		this.playVideo();

		this.watchSlider();
		this.watchViewport();
	}

	playVideo() {
		const playPromise = this.video?.play();
		if (playPromise !== undefined) {
			playPromise.catch((error) => {
				console.warn("[card-video] Video play prevented:", error);
			});
		}
	}

	pauseVideo() {
		if (this.video && !this.video.paused) this.video.pause();
	}

	pauseOtherMedia() {
		document.querySelectorAll("card-video[loaded]").forEach((cardVideo) => {
			if (cardVideo !== this) cardVideo.pauseVideo();
		});

		if (typeof window.pauseAllMedia === "function") window.pauseAllMedia();
	}

	// Swiping to another slide inside the card stops playback.
	watchSlider() {
		if (this.sliderBound) return;

		const swiper = this.closest(".swiper")?.swiper;
		if (!swiper) return;

		this.sliderBound = true;
		swiper.on("slideChange", () => this.pauseVideo());
	}

	// Scrolling the card out of the viewport stops playback.
	watchViewport() {
		if (this.viewportObserver) return;

		this.viewportObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) this.pauseVideo();
				});
			},
			{ threshold: 0 }
		);

		this.viewportObserver.observe(this);
	}
}

if (!customElements.get("card-video")) {
	customElements.define("card-video", CardVideo);
}
